import { useEffect, useState } from 'react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import {
  HiOutlineSwitchHorizontal, HiOutlineHand, HiOutlineLightningBolt, HiOutlineArrowUp, HiOutlineArrowDown,
  HiOutlineTrash, HiOutlinePause, HiOutlinePlay, HiOutlineFlag, HiOutlineUserAdd,
} from 'react-icons/hi'
import autoAssignService from '../../services/autoAssignService'
import { ROLE_LABELS } from '../../services/userService'

const initials = (name = '') => name.trim().split(/\s+/).slice(-1)[0]?.charAt(0)?.toUpperCase() || '?'

/**
 * Card "Phân luồng lead" ở /admin/settings.
 * - Thủ công: như cũ — Quản lý/Trưởng nhóm tự phân công ở /admin/leads.
 * - Tự động: lead mới giao ngay theo vòng luân phiên — Admin thêm ai (mọi vai trò) thì người đó nhận (thêm / xóa / đổi vị trí / tạm dừng).
 * Mọi thao tác lưu NGAY (không dùng nút "Lưu thay đổi" của CMS).
 */
function AutoAssignSettings() {
  const [data, setData] = useState(null)
  const [mode, setMode] = useState('manual')   // hiển thị; 'auto' khi bật hoặc đang chuẩn bị danh sách
  const [busy, setBusy] = useState(false)
  const [confirmDel, setConfirmDel] = useState(null)

  useEffect(() => {
    autoAssignService.get()
      .then((res) => { if (res.status === 'success') { setData(res.data); setMode(res.data.enabled ? 'auto' : 'manual') } })
      .catch(() => {})
  }, [])

  const save = async (body, okMsg) => {
    setBusy(true)
    try {
      const res = await autoAssignService.save(body)
      if (res.status === 'success') {
        setData(res.data)
        if (res.data.enabled) setMode('auto')
        toast.success(okMsg || res.message)
        return true
      }
    } catch { /* interceptor */ } finally { setBusy(false) }
    return false
  }

  if (!data) {
    return <div className="card p-5 flex justify-center"><div className="spinner w-6 h-6" /></div>
  }

  const members = data.members
  const pending = mode === 'auto' && !data.enabled   // đã chọn Tự động nhưng chưa có ai nhận lead
  const payload = () => members.map((m) => ({ user_id: m.user_id, is_active: m.is_active }))
  // Danh sách (sau khi sửa) còn ai nhận được lead không — người mới thêm luôn là tài khoản đang hoạt động
  const anyEligible = (list) => list.some((x) => {
    const m = members.find((y) => y.user_id === x.user_id)
    return x.is_active && (!m || m.user_active)
  })

  const saveList = (list, okMsg, extra = {}) => {
    const ok = anyEligible(list)
    if (data.enabled && !ok) {
      toast.error('Phải còn ít nhất 1 người nhận lead. Chuyển về Thủ công nếu muốn dừng hẳn.')
      return Promise.resolve(false)
    }
    return save({ members: list, ...extra, ...(pending && ok ? { enabled: true } : {}) }, okMsg)
  }

  const pickMode = async (next) => {
    if (busy || next === mode) return
    if (next === 'manual') {
      if (!data.enabled || (await save({ enabled: false }))) setMode('manual')
      return
    }
    // Tự động: đã có người nhận -> bật luôn; chưa có -> mở danh sách để thêm, bật khi thêm người đầu tiên
    if (anyEligible(payload())) await save({ enabled: true })
    else setMode('auto')
  }

  const addMember = (uid) => {
    if (!uid) return
    const cand = data.candidates.find((c) => String(c.id) === String(uid))
    saveList([...payload(), { user_id: Number(uid), is_active: true }],
      `Đã thêm ${cand?.full_name || ''} vào vòng luân phiên`)
  }

  const move = (idx, dir) => {
    const list = payload()
    const j = idx + dir
    if (j < 0 || j >= list.length) return
    ;[list[idx], list[j]] = [list[j], list[idx]]
    saveList(list, 'Đã đổi vị trí')
  }

  const togglePause = (idx) => {
    const m = members[idx]
    const list = payload()
    list[idx].is_active = !m.is_active
    saveList(list, list[idx].is_active ? `${m.full_name} nhận lead trở lại` : `Đã tạm dừng ${m.full_name}`)
  }

  const remove = async (idx) => {
    const m = members[idx]
    await saveList(payload().filter((_, k) => k !== idx), `Đã xóa ${m.full_name} khỏi vòng luân phiên`)
    setConfirmDel(null)
  }

  const setNext = (uid) => save({ next_user_id: uid }, 'Đã đặt người nhận kế tiếp')

  const nextName = members.find((m) => m.user_id === data.next_user_id)?.full_name

  return (
    <div className="card p-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
            <HiOutlineSwitchHorizontal className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-secondary-900">Phân luồng lead</h2>
            <p className="text-sm text-secondary-500">
              Cách giao hồ sơ mới cho người liên hệ - xử lý <span className="text-2xs text-secondary-400">· tự lưu khi thay đổi</span>
            </p>
          </div>
        </div>

        <div className="inline-flex p-1 rounded-lg bg-secondary-100 self-start shrink-0" role="radiogroup" aria-label="Chế độ phân luồng">
          {[
            { key: 'manual', label: 'Thủ công', icon: HiOutlineHand },
            { key: 'auto', label: 'Tự động', icon: HiOutlineLightningBolt },
          ].map(({ key, label, icon: Icon }) => (
            <button key={key} type="button" role="radio" aria-checked={mode === key} disabled={busy}
              onClick={() => pickMode(key)}
              className={clsx(
                'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all',
                mode === key
                  ? key === 'auto' ? 'bg-primary-600 text-white shadow-sm' : 'bg-white text-secondary-900 shadow-sm'
                  : 'text-secondary-500 hover:text-secondary-800',
              )}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </div>

      {mode === 'manual' ? (
        <p className="text-sm text-secondary-600 bg-secondary-50 rounded-lg px-4 py-3">
          Hồ sơ mới vào danh sách <b>chưa phân luồng</b> — Quản lý / Trưởng nhóm tự phân công ở trang Ứng viên (như hiện tại).
          {members.length > 0 && <span className="text-secondary-400"> Vòng luân phiên ({members.length} người) vẫn được giữ, bật Tự động để dùng lại.</span>}
        </p>
      ) : (
        <div className="space-y-3">
          <div className={clsx('text-sm rounded-lg px-4 py-3',
            data.enabled ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800')}>
            {data.enabled ? (
              <>Hồ sơ mới được <b>giao ngay</b> cho nhân viên theo thứ tự bên dưới (1 → 2 → … → quay lại 1).
                {nextName && <> Người nhận kế tiếp: <b>{nextName}</b>.</>}</>
            ) : (
              <>Thêm ít nhất 1 nhân viên vào vòng luân phiên — chế độ <b>Tự động</b> sẽ bật ngay khi thêm.</>
            )}
          </div>

          {members.length > 0 && (
            <ol className="divide-y divide-secondary-100 border border-secondary-100 rounded-lg">
              {members.map((m, idx) => {
                const isNext = data.enabled && m.user_id === data.next_user_id
                const warn = !m.user_active ? 'Tài khoản đã khóa' : null
                return (
                  <li key={m.user_id}
                    className={clsx('flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5', !m.eligible && 'bg-secondary-50/70')}>
                    <span className="w-6 text-center text-sm font-semibold text-secondary-400">{idx + 1}</span>
                    <span className={clsx('avatar w-8 h-8 text-xs shrink-0', !m.eligible && 'opacity-50')}>{initials(m.full_name)}</span>
                    <div className="flex-1 min-w-[140px]">
                      <div className={clsx('text-sm font-medium flex flex-wrap items-center gap-1.5', m.eligible ? 'text-secondary-900' : 'text-secondary-400')}>
                        {m.full_name}
                        {isNext && <span className="badge-primary !py-0.5"><HiOutlineFlag className="w-3 h-3" /> Kế tiếp</span>}
                        {!m.is_active && <span className="badge-warning !py-0.5">Tạm dừng</span>}
                        {warn && <span className="badge-danger !py-0.5">{warn}</span>}
                      </div>
                      <div className="text-2xs text-secondary-400">
                        {m.username && <>@{m.username}</>}{m.role && m.role !== 'member' && <> · {ROLE_LABELS[m.role] || m.role}</>}{m.team_name && <> · {m.team_name}</>}
                        <> · Hôm nay: <b className="text-secondary-600">{m.today_count}</b> hồ sơ</>
                      </div>
                    </div>

                    <div className="flex items-center gap-0.5 ml-auto">
                      {data.enabled && m.eligible && !isNext && (
                        <button type="button" disabled={busy} onClick={() => setNext(m.user_id)} title="Cho người này nhận hồ sơ kế tiếp"
                          className="p-2 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg disabled:opacity-40">
                          <HiOutlineFlag className="w-4 h-4" />
                        </button>
                      )}
                      <button type="button" disabled={busy || idx === 0} onClick={() => move(idx, -1)} title="Lên trên"
                        className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg disabled:opacity-30">
                        <HiOutlineArrowUp className="w-4 h-4" />
                      </button>
                      <button type="button" disabled={busy || idx === members.length - 1} onClick={() => move(idx, 1)} title="Xuống dưới"
                        className="p-2 text-secondary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg disabled:opacity-30">
                        <HiOutlineArrowDown className="w-4 h-4" />
                      </button>
                      <button type="button" disabled={busy} onClick={() => togglePause(idx)}
                        title={m.is_active ? 'Tạm dừng nhận lead (nghỉ phép...)' : 'Nhận lead trở lại'}
                        className="p-2 text-secondary-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg disabled:opacity-40">
                        {m.is_active ? <HiOutlinePause className="w-4 h-4" /> : <HiOutlinePlay className="w-4 h-4" />}
                      </button>
                      {confirmDel === m.user_id ? (
                        <span className="inline-flex items-center gap-1 pl-1">
                          <button type="button" disabled={busy} onClick={() => remove(idx)} className="btn-danger btn-sm">Xóa</button>
                          <button type="button" onClick={() => setConfirmDel(null)} className="btn-ghost btn-sm">Hủy</button>
                        </span>
                      ) : (
                        <button type="button" disabled={busy} onClick={() => setConfirmDel(m.user_id)} title="Xóa khỏi vòng luân phiên"
                          className="p-2 text-secondary-500 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-40">
                          <HiOutlineTrash className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ol>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="relative w-full sm:w-80 shrink-0">
              <HiOutlineUserAdd className="w-4 h-4 text-secondary-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select className="select pl-9" value="" disabled={busy || data.candidates.length === 0}
                onChange={(e) => addMember(e.target.value)}>
                <option value="">
                  {data.candidates.length ? 'Thêm người vào vòng…' : 'Đã thêm hết tài khoản đang hoạt động'}
                </option>
                {data.candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.full_name}{c.username ? ` (@${c.username})` : ''}{c.role && c.role !== 'member' ? ` · ${ROLE_LABELS[c.role] || c.role}` : ''}{c.team_name ? ` · ${c.team_name}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-2xs text-secondary-400">
              Ai có trong vòng đều nhận lead (mọi vai trò). Hồ sơ từ chiến dịch của nhân viên vẫn về người tạo chiến dịch.
            </p>
          </div>
        </div>
      )}

      {data.updated_by_name && (
        <p className="text-2xs text-secondary-400 mt-3">
          Cập nhật lần cuối bởi {data.updated_by_name}
          {data.updated_at && <> · {new Date(data.updated_at).toLocaleString('vi-VN')}</>}
        </p>
      )}
    </div>
  )
}

export default AutoAssignSettings
